import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LayoutDashboard, LogOut, ShieldCheck, UserCog } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import LanguageToggle from './LanguageToggle';
import Logo from './brand/Logo';

const Header: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();


  // Navigation is role-aware: a homeowner account never sees the contractor
  // lead marketplace or the contractor membership pricing page.
  //
  // "Commercial" is shown to EVERY visitor (including homeowner accounts),
  // because a homeowner can also be the person who manages a retail space,
  // restaurant or office building. It points at the commercial audience
  // landing page, not at a filtered feed, so it is useful signed out too.
  const homeowner = profile?.user_type === 'homeowner' && !profile?.is_admin;
  const links = [
    ...(homeowner ? [] : [{ to: path('/jobs'), label: t('nav.jobs') }]),
    { to: path('/commercial'), label: t('nav.commercial') },
    { to: path('/how-it-works'), label: t('nav.howItWorks') },
    ...(homeowner ? [] : [{ to: path('/pricing'), label: t('nav.pricing') }]),
    { to: path('/contact'), label: t('nav.contact') },
  ];




  const handleLogout = async () => {
    await signOut();
    setOpen(false);
    navigate(path('/'));
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Lockup comes from the shared brand components, so the header, the
            footer, the favicon and the brand studio can never disagree. */}
        <Link to={path('/')} onClick={() => setOpen(false)} aria-label="GIGGONE">
          <Logo tone="light" size={36} />
        </Link>


        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageToggle />
          {profile ? (
            <>
              {profile.is_admin && (
                <Link
                  to={path('/admin')}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
                >
                  <ShieldCheck className="h-4 w-4" /> {t('nav.admin')}
                </Link>
              )}
              <Link
                to={path('/profile')}
                className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
              >
                <UserCog className="h-4 w-4" /> {lang === 'es' ? 'Perfil' : 'Profile'}
              </Link>
              <Link
                to={path('/dashboard')}
                className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"
              >
                <LayoutDashboard className="h-4 w-4" /> {t('nav.dashboard')}
              </Link>

              <button
                onClick={handleLogout}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                aria-label={t('nav.logout')}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link to={path('/login')} className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900">
                {t('nav.login')}
              </Link>
              <Link
                to={path('/register')}
                className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-orange-600"
              >
                {t('nav.signup')}
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <LanguageToggle compact />
          <button
            onClick={() => setOpen((o) => !o)}
            className="rounded-lg p-2 text-slate-700 hover:bg-slate-100"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white md:hidden">
          <div className="space-y-1 px-4 py-3">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="block rounded-lg px-3 py-2.5 text-base font-semibold text-slate-700 hover:bg-slate-50"
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-2 grid gap-2 border-t border-slate-100 pt-3">
              {profile ? (
                <>
                  {profile.is_admin && (
                    <Link
                      to={path('/admin')}
                      onClick={() => setOpen(false)}
                      className="rounded-lg border border-slate-200 px-3 py-2.5 text-center text-base font-bold text-slate-800"
                    >
                      {t('nav.admin')}
                    </Link>
                  )}
                  <Link
                    to={path('/profile')}
                    onClick={() => setOpen(false)}
                    className="rounded-lg border border-slate-200 px-3 py-2.5 text-center text-base font-bold text-slate-800"
                  >
                    {lang === 'es' ? 'Configuración de Perfil' : 'Profile Settings'}
                  </Link>
                  <Link
                    to={path('/dashboard')}
                    onClick={() => setOpen(false)}
                    className="rounded-lg bg-slate-900 px-3 py-2.5 text-center text-base font-bold text-white"
                  >
                    {t('nav.dashboard')}
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="rounded-lg border border-slate-200 px-3 py-2.5 text-base font-bold text-slate-700"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to={path('/login')}
                    onClick={() => setOpen(false)}
                    className="rounded-lg border border-slate-200 px-3 py-2.5 text-center text-base font-bold text-slate-800"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to={path('/register')}
                    onClick={() => setOpen(false)}
                    className="rounded-lg bg-orange-500 px-3 py-2.5 text-center text-base font-bold text-white"
                  >
                    {t('nav.signup')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
